import type { Fetcher } from '@/lib/api/fetcher';

import type { ArMode, Conversion, NailArtDesign, Render, Shade, TryOnSession } from './schemas';
import {
  conversionSchema,
  designListSchema,
  renderSchema,
  sessionSchema,
  shadeListSchema,
} from './schemas';

/** One function per BP-21 §5 route the §6 screen uses. */
export interface TryOnApi {
  listShades: () => Promise<Shade[]>;
  openSession: (open: OpenSession) => Promise<TryOnSession>;
  patchSession: (sessionId: string, patch: SessionPatch) => Promise<TryOnSession>;
  conversion: (window?: ReportWindow) => Promise<Conversion>;
  /** BP-22 §5: upload one photograph and queue the render. */
  createRender: (photo: Blob, options?: RenderOptions) => Promise<Render>;
  readRender: (renderId: string) => Promise<Render>;
  listDesigns: () => Promise<NailArtDesign[]>;
}

export interface RenderOptions {
  shadeSkuId?: string;
  designId?: string;
  sessionId?: string;
}

export interface OpenSession {
  mode: ArMode;
  device_class?: string;
  model_version_id?: string;
}

export interface SessionPatch {
  shades_tried?: string[];
  fps_avg?: string;
  booking_id?: string;
  ended?: boolean;
}

export interface ReportWindow {
  start?: string;
  end?: string;
}

export const PREFIX = '/v1/ar';

/** `?start=&end=`, omitting what was not asked for (BP-16's reason). */
export function reportQuery(window?: ReportWindow): string {
  const params = new URLSearchParams();
  for (const key of ['start', 'end'] as const) {
    const value = window?.[key];
    if (value !== undefined) {
      params.set(key, value);
    }
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/**
 * The multipart body `POST /ar/renders` takes.
 *
 * Built here rather than in the screen so the field names live beside the
 * route that reads them — `photo`, `shade_sku_id`, `design_id`,
 * `session_id` — and so no screen has to know there is no `client_id`
 * field, which is the one thing about this body that matters.
 */
export function renderBody(photo: Blob, options: RenderOptions = {}): FormData {
  const form = new FormData();
  form.append('photo', photo, 'hand.jpg');
  const fields = {
    shade_sku_id: options.shadeSkuId,
    design_id: options.designId,
    session_id: options.sessionId,
  };
  for (const [name, value] of Object.entries(fields)) {
    if (value !== undefined) {
      form.append(name, value);
    }
  }
  return form;
}

/** The §5 routes over the shared transport. */
export function createTryOnApi(fetcher: Fetcher): TryOnApi {
  return {
    listShades: () => fetcher.json(`${PREFIX}/shades`, { method: 'GET' }, shadeListSchema),
    openSession: (open) =>
      fetcher.json(`${PREFIX}/sessions`, { method: 'POST', body: open }, sessionSchema),
    patchSession: (sessionId, patch) =>
      fetcher.json(
        `${PREFIX}/sessions/${sessionId}`,
        { method: 'PATCH', body: patch },
        sessionSchema,
      ),
    conversion: (window) =>
      fetcher.json(
        `${PREFIX}/sessions/conversion${reportQuery(window)}`,
        { method: 'GET' },
        conversionSchema,
      ),
    createRender: (photo, options) =>
      fetcher.json(
        `${PREFIX}/renders`,
        { method: 'POST', body: renderBody(photo, options) },
        renderSchema,
      ),
    readRender: (renderId) =>
      fetcher.json(`${PREFIX}/renders/${renderId}`, { method: 'GET' }, renderSchema),
    listDesigns: () => fetcher.json(`${PREFIX}/nail-art`, { method: 'GET' }, designListSchema),
  };
}
